import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tvApi } from "../lib/api";
import { Camera, Loader2, AlertTriangle } from "lucide-react";

/**
 * TVPairing — the page the host opens directly on the TV browser.
 * Shows a 6-char pairing code that the host then enters from their
 * phone (Dashboard → Conectar TV). Polls /api/tv/status until paired,
 * then redirects to /tv/<event_id>?tv_token=...
 */
export default function TVPairing() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | waiting | expired | error
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const requestNewCode = async () => {
    setStatus("loading");
    setError(null);
    try {
      const { data } = await tvApi.requestCode("TV Festa");
      setCode(data.code);
      setExpiresIn(data.expires_in_seconds || 900);
      setStatus("waiting");
    } catch (e) {
      console.error(e);
      setError("Nao foi possivel gerar codigo. Verifique a conexao.");
      setStatus("error");
    }
  };

  // On mount: if this TV was previously paired, auto-redirect to its
  // event without showing a new code. Verify the token is still valid;
  // if not, fall back to requesting a fresh code as usual.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let savedToken = null;
        let savedEventId = null;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("festa_tv_token_")) {
            savedToken = localStorage.getItem(key);
            savedEventId = key.replace("festa_tv_token_", "");
            break;
          }
        }
        if (savedToken && savedEventId) {
          try {
            await tvApi.verifyToken(savedToken);
            if (!cancelled) {
              navigate(`/tv/${savedEventId}?tv_token=${encodeURIComponent(savedToken)}`);
            }
            return;
          } catch (_) {
            // Token expired/invalid — remove and fall through to fresh code
            try { localStorage.removeItem(`festa_tv_token_${savedEventId}`); } catch (e) {}
          }
        }
      } catch (_) { /* ignore */ }
      if (!cancelled) requestNewCode();
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown + poll while waiting
  useEffect(() => {
    if (status !== "waiting" || !code) return;

    // countdown
    tickRef.current = setInterval(() => {
      setExpiresIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    // poll
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await tvApi.checkStatus(code);
        if (data.status === "paired" && data.event_id && data.tv_token) {
          // Persist token and redirect
          try {
            localStorage.setItem(`festa_tv_token_${data.event_id}`, data.tv_token);
          } catch (_) {}
          navigate(`/tv/${data.event_id}?tv_token=${encodeURIComponent(data.tv_token)}`);
        } else if (data.status === "expired") {
          setStatus("expired");
        }
      } catch (e) {
        // tolerate transient errors; keep polling
      }
    }, 2500);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [status, code, navigate]);

  // Auto-refresh when expired (give the user fresh code without clicking)
  useEffect(() => {
    if (status === "expired") {
      const t = setTimeout(requestNewCode, 1500);
      return () => clearTimeout(t);
    }
  }, [status]);

  const mins = Math.floor(expiresIn / 60);
  const secs = expiresIn % 60;
  const timeStr = `${mins}:${String(secs).padStart(2, "0")}`;
  const codeBlocks = code ? [code.slice(0, 3), code.slice(3)] : ["---", "---"];

  return (
    <div className="min-h-screen bg-[#1C1917] text-white flex flex-col" data-testid="tv-pairing-page">
      <header className="px-8 py-6 flex items-center gap-3">
        <div className="w-10 h-10 bg-[#FF6B6B] border-2 border-white rounded-lg flex items-center justify-center">
          <Camera className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <span className="font-black text-2xl tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Festa.IA
        </span>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="max-w-3xl w-full text-center">
          <p className="text-[#A8A29E] uppercase tracking-[0.3em] font-bold text-sm mb-4">
            Conectar esta TV
          </p>
          <h1
            className="text-4xl md:text-5xl font-black mb-10 leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Digite este codigo no celular
          </h1>

          {/* Code display */}
          <div className="bg-white text-[#1C1917] border-4 border-[#FF6B6B] rounded-3xl py-12 px-6 mb-8 shadow-[10px_10px_0px_#FF6B6B] inline-block">
            {status === "loading" && (
              <Loader2 className="w-16 h-16 animate-spin text-[#FF6B6B] mx-auto" />
            )}
            {status === "waiting" && (
              <div
                className="flex items-center justify-center gap-4 md:gap-6 font-black text-7xl md:text-9xl tracking-[0.2em] font-mono"
                data-testid="tv-pairing-code"
              >
                <span>{codeBlocks[0]}</span>
                <span className="text-[#FF6B6B]">-</span>
                <span>{codeBlocks[1]}</span>
              </div>
            )}
            {status === "expired" && (
              <div className="text-2xl font-bold text-[#78716C]">
                Codigo expirado. Gerando novo...
              </div>
            )}
            {status === "error" && (
              <div className="flex flex-col items-center gap-3 text-[#FF6B6B]">
                <AlertTriangle className="w-12 h-12" strokeWidth={2.5} />
                <span className="text-xl font-bold">{error}</span>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-[#292524] border-2 border-[#44403C] rounded-2xl px-6 py-6 text-left max-w-xl mx-auto">
            <p className="text-[#A8A29E] uppercase font-bold text-xs tracking-wider mb-3">
              Como conectar
            </p>
            <ol className="space-y-2.5 text-base">
              <li className="flex gap-3">
                <span className="bg-[#FF6B6B] text-white w-7 h-7 rounded-full flex items-center justify-center font-black text-sm shrink-0">1</span>
                <span>Abra a Festa.IA no celular e entre como anfitriao</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-[#FF6B6B] text-white w-7 h-7 rounded-full flex items-center justify-center font-black text-sm shrink-0">2</span>
                <span>No Dashboard, toque em <strong className="text-white">"Conectar TV"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="bg-[#FF6B6B] text-white w-7 h-7 rounded-full flex items-center justify-center font-black text-sm shrink-0">3</span>
                <span>Digite o codigo acima e escolha qual evento exibir</span>
              </li>
            </ol>
          </div>

          {status === "waiting" && (
            <p className="text-[#78716C] text-sm mt-6" data-testid="tv-pairing-countdown">
              Codigo valido por {timeStr}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
